import { type EntityPrefill } from './entity-prefill.types';
import { type HelpRef } from './help-ref.model';

export interface ChatHistoryMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** Structural UI route supplied by the client. Never contains tenant data. */
export interface ChatAvailableRoute {
  path: string;
  label?: string;
  title?: string;
}

/** Structural detail UI available to current user. Never contains entity data. */
export interface ChatAvailableEntityDetail {
  entity: string;
  route: string;
  title: string;
  sections: ChatAvailableEntityDetailSection[];
  actions: ChatAvailableEntityDetailAction[];
}

export interface ChatAvailableEntityDetailSection {
  id: string;
  title: string;
  requiresEntityId?: boolean;
}

export interface ChatAvailableEntityDetailAction {
  id: string;
  label: string;
  sectionId?: string;
  requiresEntityId?: boolean;
  intent: 'navigate' | 'mutation';
}

export interface ChatRequest {
  userMessage: string;
  conversationHistory?: ChatHistoryMessage[];
  /** User's current language (e.g., 'en', 'de', 'fr', 'it') for localized formatting */
  language?: string;
  /** Current frontend route (e.g., '/persons', '/planning/plans') for context-aware responses */
  currentRoute?: string;
  /** Routes that current user can access, including discovered lazy child routes. */
  availableRoutes?: ChatAvailableRoute[];
  /** Permission-filtered, translated static detail-page structure. */
  availableEntityDetails?: ChatAvailableEntityDetail[];
}

export interface ChatSuggestedLink {
  label: string;
  url: string;
}

/**
 * Preview of a proposed mutation shown on the chat confirmation card.
 * Structurally identical to `PlanningCommandPreview` from `@zag/planning/common`
 * (kept generic here so the AI models lib stays domain-agnostic).
 */
export interface PendingActionPreview {
  changes: Array<{ date: string; employeeId: string; shiftUnitId: string; action: 'added' | 'removed' }>;
  conflicts: Array<{ type: string; date: string; personId: string; eventLabel: string }>;
  summary: {
    assignmentsAdded: number;
    assignmentsRemoved: number;
    affectedDates: string[];
    affectedEmployees: string[];
  };
}

/**
 * A validated, structured action the assistant is suggesting.
 * Replaces raw markdown links with typed, permission-validated objects.
 *
 * `confirm-pending-action` renders the human-in-the-loop confirmation card:
 * the mutation proposal is persisted server-side and only executes through
 * the confirmation endpoint.
 */
export interface ChatAction {
  /** Action type discriminator */
  type: 'navigate' | 'create' | 'search' | 'confirm-pending-action';
  /** Human-readable label for the action */
  label: string;
  /** Application route for navigate/create actions */
  route?: string;
  /** Search scope ID for search actions */
  scopeId?: string;
  /** Material icon name */
  icon?: string;
  /** ID of the persisted pending action (confirm-pending-action only) */
  pendingActionId?: string;
  /** Proposal preview rendered on the confirmation card (confirm-pending-action only) */
  preview?: PendingActionPreview;
  /** ISO timestamp after which the pending action can no longer be confirmed */
  expiresAt?: string;
  /** Client-tracked lifecycle state of the pending action (updated after confirm/cancel). */
  pendingActionStatus?: PendingActionStatus;
}

/** Lifecycle states of a pending action (mirrors ai.pending_action_status). */
export type PendingActionStatus = 'proposed' | 'executed' | 'failed' | 'cancelled' | 'expired';

/** Response shape of the pending-action endpoints. */
export interface PendingActionState {
  id: string;
  toolName: string;
  status: PendingActionStatus;
  preview: PendingActionPreview;
  resultSummary: { error?: string; jobId?: string; applied?: { instanceId: string; updatedAt: string } } | null;
  expiresAt: string;
  executedAt: string | null;
  createdAt: string;
}

/**
 * Provenance metadata explaining where an answer came from.
 */
export interface ChatSource {
  /** Whether the source is a live tool result or a capability graph entry */
  type: 'tool' | 'capability';
  /** Internal tool/scope name */
  name: string;
  /** Human-readable label */
  label: string;
  /** ISO timestamp of the data (for tool results) */
  timestamp?: string;
}

/**
 * A structured fact card summarizing a live data result.
 */
export interface ChatFactCard {
  type: 'coverage-gap' | 'schedule-summary' | 'person' | 'team' | 'notification-summary' | 'calendar-summary';
  title: string;
  summary: string;
  data?: Record<string, unknown>;
}

/**
 * Assistant request intent classification.
 * 'action' = the user asks for a plan change; routed through the tool-calling
 * loop where mutations only produce confirmable proposals.
 */
export type ChatIntentType = 'navigation' | 'fact' | 'hybrid' | 'off-topic' | 'action';

export interface ChatResponse {
  message: string;
  isOffTopic?: boolean;
  /**
   * Legacy suggestedLinks kept for backward compatibility during migration.
   * Prefer `actions` for new UI rendering.
   */
  suggestedLinks?: ChatSuggestedLink[];
  entityPrefills?: EntityPrefill[];
  /**
   * Validated structured actions returned by the assistant.
   * All actions have been validated against existing routes and user permissions.
   */
  actions?: ChatAction[];
  /**
   * Fact cards from live operational tool results.
   */
  factCards?: ChatFactCard[];
  /**
   * Source metadata explaining whether the answer came from capability graph or live tools.
   */
  sources?: ChatSource[];
  /**
   * Intent classification of the request.
   */
  intentType?: ChatIntentType;
  /**
   * In-app help topics the user is asking about (e.g. "how do I set up
   * Microsoft sign-in?"). The chat UI renders the matching `HelpStepsComponent`
   * inline below the assistant message so the user sees the same canonical
   * content as the help dialog — never paraphrased by the model.
   */
  helpRefs?: HelpRef[];
}
